import { useState, useMemo } from 'react';
import { useMarketAggregates } from '../../contexts/MarketAggregatesContext';
import {
  generateDates,
  getVisibleDates,
  isAtFirstDate,
  isAtLastDate,
  getMaxDateIndex,
} from './utils';
import { useDemo } from '@app/components/demo/DemoProvider';

/**
 * Custom hook for managing date navigation state and logic
 */
export const useDateNavigation = (
  totalDays: number = 7,
  visibleCount: number = 2,
  tabId?: string,
) => {
  const [currentDateIndex, setCurrentDateIndex] = useState(() => {
    if (!tabId) return 5; // Start showing most recent dates
    try {
      const raw = localStorage.getItem(`ma-models-dateindex-${tabId}`);
      if (!raw) return 5;
      const parsed = JSON.parse(raw);
      return typeof parsed === 'number' ? parsed : 5;
    } catch {
      return 5;
    }
  });

  const { isDemo } = useDemo();
  const dates = useMemo(() => generateDates(totalDays, isDemo), [totalDays, isDemo]);
  const currentDates = useMemo(
    () => getVisibleDates(dates, currentDateIndex, visibleCount),
    [dates, currentDateIndex, visibleCount],
  );
  const maxIndex = useMemo(
    () => getMaxDateIndex(totalDays, visibleCount),
    [totalDays, visibleCount],
  );

  const isAtFirst = useMemo(() => isAtFirstDate(currentDateIndex), [currentDateIndex]);
  const isAtLast = useMemo(
    () => isAtLastDate(currentDateIndex, maxIndex),
    [currentDateIndex, maxIndex],
  );

  const handleNextDate = () => {
    setCurrentDateIndex((prev) => Math.min(prev + 1, maxIndex));
  };

  const handlePreviousDate = () => {
    setCurrentDateIndex((prev) => Math.max(0, prev - 1));
  };

  // Persist selection per tab
  if (tabId) {
    try {
      localStorage.setItem(`ma-models-dateindex-${tabId}`, JSON.stringify(currentDateIndex));
    } catch {
      // Intentionally ignore localStorage errors
    }
  }

  return {
    currentDateIndex,
    currentDates,
    isAtFirstDate: isAtFirst,
    isAtLastDate: isAtLast,
    handleNextDate,
    handlePreviousDate,
  };
};

/**
 * Custom hook for managing model selection and actions
 */
export const useModelActions = () => {
  const { state, toggleModelSelection, updateModelRuns, resetAllSelections } =
    useMarketAggregates();

  const handleResetAll = () => {
    resetAllSelections();
  };

  const handleModelToggle = (modelId: string) => {
    toggleModelSelection(modelId);
  };

  const handleSelectionChange = (modelId: string, runs: { date: string; time: string }[]) => {
    updateModelRuns(modelId, runs);
  };

  return {
    state,
    handleResetAll,
    handleModelToggle,
    handleSelectionChange,
  };
};
